<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Charts</span>
      <h1>Interactive Steam Charts for Personal Playtime & Trends</h1>
      <p>
        Watch your gaming story unfold with interactive Steam charts. From rolling playtime totals to per-title comparisons,
        steamplayti.me turns raw numbers into visuals you can actually use.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Generate your charts</router-link>
        <a class="seo-secondary-link" href="#chart-gallery">Chart gallery →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">📊</span>
          <div>Stacked area charts show how different games compete for time across the weeks.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🕒</span>
          <div>Time filters let you zoom into the last seven days or zoom out to your entire Steam lifetime.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🔗</span>
          <div>Share any chart snapshot with one URL — perfect for Discord flexes or community check-ins.</div>
        </li>
      </ul>
    </section>

    <SeoTopGamesInsights />

    <section class="seo-section" id="chart-gallery">
      <h2>A Gallery of Steam Charts</h2>
      <p>
        We ship multiple chart types so every player can read their playtime the way they prefer.
        Slice the data for personal dashboards, streaming overlays, or weekly blog posts.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Daily Activity Chart</h3>
          <p>Track active days at a glance and spot streaks you never thought you had.</p>
        </div>
        <div class="seo-card">
          <h3>Top Game Distribution</h3>
          <p>Pie and bar charts outline which games dominate your schedule and which ones need love.</p>
        </div>
        <div class="seo-card">
          <h3>Backlog Trendline</h3>
          <p>See whether you are catching up or falling behind on unfinished titles week by week.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Charts with Context</h2>
      <p>
        Numbers without context do not help. That is why every chart on steamplayti.me ships with annotations,
        totals, and comparisons to keep you grounded.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Chart types</div>
          <div class="value">5+</div>
        </div>
        <div class="seo-stat">
          <div class="label">Update cadence</div>
          <div class="value">Live</div>
        </div>
        <div class="seo-stat">
          <div class="label">Export ready</div>
          <div class="value">PNG/CSV</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Questions About Steam Charts</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>Can I embed these charts?</h3>
          <p>Yes. Copy the share link into OBS, blogs, or social posts. We keep the data live so charts always stay fresh.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Do the charts work on mobile?</h3>
          <p>The dashboard is responsive, so you can review Steam charts comfortably on phones and tablets.</p>
        </div>
        <div class="seo-faq-item">
          <h3>What if I prefer raw data?</h3>
          <p>Download CSV exports for deep dives or import the numbers into your own visualizations at any moment.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-charts" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';
import SeoTopGamesInsights from '../components/SeoTopGamesInsights.vue';

const META_DESCRIPTION = 'Generate Steam charts for daily activity, top games, and backlog trends. steamplayti.me transforms raw playtime into visuals you can share or analyze.';

const META = {
  title: 'Steam Charts & Playtime Visualizations | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'Steam charts, Steam playtime charts, Steam stats visualization, Steam data charts, Steam playtime graph',
  canonical: 'https://steamplayti.me/page/steam-charts/',
  ogTitle: 'Steam Charts & Playtime Visualizations',
  ogDescription: 'Create interactive Steam charts with live playtime data, shareable snapshots, and export-friendly formats.',
  ogUrl: 'https://steamplayti.me/page/steam-charts/',
  twitterTitle: 'Steam Charts & Playtime Visualizations',
  twitterDescription: 'Build Steam playtime charts and share interactive visuals instantly with steamplayti.me.',
  structuredData: pageStructuredData({
    name: 'Steam Charts',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-charts/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Charts', path: 'https://steamplayti.me/page/steam-charts/' },
    ],
  }),
};

export default {
  name: 'SteamChartsPage',
  components: { SeoRelatedTools, SeoTopGamesInsights },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
